import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Tooltip,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "./useAuth";

import { getStyles } from "../index";

import SettingsPopover from "./SettingsPopover";

import UsageBox from "./UsageBox";

import NewFunctionDialog from "./NewFunctionDialog";

import TeacherSearch from "./TeacherSearch";
import GridLayout from "./GridLayout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CustomInputSwitch from "./CustomInputSwitch";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import NotificationComponent from "./NotificationsComponent";
import Logger from "./Logger";

import AccountTypeDialog from "./UserTypeSelection";

import OnboardingProgress from "./OnboardingProgress";
import MenuOpenIcon from "@mui/icons-material/MenuBook";

import { getDoc, doc } from "firebase/firestore";
import { db } from "./firebase";

import FeedbackDialog from "./FeedbackDialog";

import SchoolIcon from "@mui/icons-material/School";

import CourseList from "./CourseList";

import { Link, useNavigate } from "react-router-dom";

import { useSnackbar } from "./SnackbarHandler";

import FeedbackPaper from "./FeedbackPaper";

import { styled } from "@mui/material/styles";

const Dashboard = ({
  onUpdateUser,
  setOverrideTheme,
  user,
  toggleMinimizedSidebar,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [role, setRole] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [resetCategory, setResetCategory] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openPopover = Boolean(anchorEl);

  const [rooms, setRooms] = useState("");
  const [newFunctionDialogOpen, setNewFunctionDialogOpen] = useState(false);

  const [currentAvatarUrl, setCurrentAvatarUrl] = useState(user?.photoURL);

  const handleAvatarUpdate = (newAvatarUrl) => {
    setCurrentAvatarUrl(newAvatarUrl);
  };

  const maxTokens = user.guest
    ? process.env.REACT_APP_GUEST_TOKEN_LIMIT
    : process.env.REACT_APP_REGISTERED_TOKEN_LIMIT;
  const usedTokens = user.tokens;
  const remainingTokens = maxTokens - usedTokens;
  const usedPercentage = (usedTokens / maxTokens) * 100;
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [showAccountTypeDialog, setShowAccountTypeDialog] = useState(false);
  const [accountType, setAccountType] = useState(
    user?.accountType || "teacher",
  );

  useEffect(() => {
    Logger.log("Guest: ", user.guest);
    Logger.log("Municipality ", user.municipality);
    Logger.log("School ", user.school);
    Logger.log("Account type: ", user.accountType);
    Logger.log("Teaching level: ", user.teachingLevel);
    Logger.log("Teaching subjects: ", user.subjects);

    const params = new URLSearchParams(window.location.search);
    const hasPartnerData = params.get("userData");

    // Don't show dialog if we're in the SSO flow
    if (hasPartnerData) {
      return;
    }

    if (
      !user.guest &&
      (!user?.municipality ||
        !user?.school ||
        user?.accountType === "undecided")
    ) {
      Logger.log(
        "user in useeffect in dashboard that should get accountTypeDialog: ",
        JSON.stringify(user, null, 2),
      );
      setShowAccountTypeDialog(true);
    }
  }, [user]);

  // useEffect(() => {
  //   const checkShowNewFunctionDialog = () => {
  //     const hasSeenNewFunction = localStorage.getItem("hasSeenNewFunction7");

  //     // Add a direct check for the onboarding condition itself here
  //     const needsOnboarding =
  //       !user.guest &&
  //       (!user?.municipality ||
  //         !user?.school ||
  //         user?.accountType === "undecided");

  //     // Only show if user hasn't seen it AND doesn't need onboarding
  //     if (!hasSeenNewFunction && !needsOnboarding) {
  //       setTimeout(() => {
  //         setNewFunctionDialogOpen(true);
  //       }, 1500);
  //     }
  //   };

  //   checkShowNewFunctionDialog();
  // }, [user]);

  // Add a function to handle closing the dialog
  // const handleCloseNewFunctionDialog = () => {
  //   setNewFunctionDialogOpen(false);
  //   localStorage.setItem("hasSeenNewFunction7", "true");
  // };

  useEffect(() => {
    Logger.log("user updated in dashboard: ", user.displayName);
    Logger.log("acc type: ", user.accountType);
  }, [user]);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAccountSetup = async (data) => {
    try {
      const userUpdateData = {
        accountType: data.accountType,
        municipality: data.municipality,
        school: data.school,
      };

      // Add teacher-specific fields if the account type is teacher
      if (data.accountType === "teacher") {
        userUpdateData.teachingLevel = data.teachingLevel;
        userUpdateData.subjects = data.subjects;
      }
      Logger.log(
        "handleAccountSetup (callback from welcomed dialog): ",
        JSON.stringify(userUpdateData, null, 2),
      );

      await onUpdateUser(userUpdateData);
      handleRoleChange(data.accountType);

      setTimeout(() => {
        setShowAccountTypeDialog(false);
      }, 600);
    } catch (error) {
      console.error("Error updating user type:", error);
    }
  };

  useEffect(() => {
    const userRef = doc(db, "users", user.uid);
    getDoc(userRef).then(async (userDoc) => {
      setRole(
        userDoc.exists() ? userDoc.data().accountType || "teacher" : "teacher",
      );

      // Get room names
      const roomIds = userDoc.data()?.rooms || [];
      const rooms = await Promise.all(
        roomIds.map((id) => getDoc(doc(db, "rooms", id))),
      );
      const names = rooms.map((room) => room.data().name).join(", ");
      setRooms(names); // Or use setState if you want
    });
  }, [user.uid]);

  const handleRoleChange = (newRole) => {
    setRole(newRole);

    // Just update the accountType field - NO SPREADING THE USER
    onUpdateUser({
      accountType: newRole,
    });

    setResetCategory((prev) => !prev);
  };

  // Don't render anything until we have the role
  if (role === null) return null;

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.freshBox,
          borderRadius: 0,
          borderRight: 0,
          borderLeft: 0,
          backgroundColor: theme.palette.mode === "dark" ? "#192734" : "white",
          borderTop: 0,
          ml: { xs: 0, md: "-30px" },
          width: { xs: "100%", md: "calc(100% + 30px)" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row", md: "row" },
          alignItems: { xs: "stretch", sm: "center", md: "center" },
          gap: { xs: 2, sm: 1, md: 1 },
          height: { xs: "68px", sm: "auto", md: "auto" },
        }}
      >
        <Box
          onClick={() => navigate("/app")}
          sx={{
            ml: { xs: 5 },
            position: "relative",
            top: { xs: "50%", sm: 0 },
            transform: { xs: "translateY(-50%)", sm: "none" },
            display: { xs: "block", md: "none", sm: "none" },
          }}
        >
          <img
            src={
              theme.palette.mode === "dark"
                ? "/img/logo-dark-2.png"
                : "/img/logo-2.png"
            }
            alt="studera.ai"
            style={{
              height: "28px",
              width: "auto",
              display: "block",
            }}
          />
        </Box>
        <Box
          sx={{
            marginLeft: "10px",
            position: { xs: "absolute", sm: "static" },
            top: { xs: 12, sm: "auto" },
            right: { xs: 68, sm: "auto" },
          }}
        >
          {/*
          <IconButton onClick={toggleMinimizedSidebar}>
            <SchoolIcon sx={{ color: "text.secondary" }} />
          </IconButton>
*/}
          <NotificationComponent
            currentUserId={user.uid}
            styles={styles}
            theme={theme}
            showSnackbar={showSnackbar}
          />
        </Box>
        <Box
          sx={{
            position: { xs: "absolute", sm: "relative" },
            borderRadius: "50%",
            padding: "0px",
            marginLeft: { xs: "13px", sm: "8px" },
            marginRight: "8px",
            display: "inline-block",
            cursor: "pointer",
            "&:hover": {
              filter: "brightness(0.85)",
            },
            top: { xs: 12, sm: "auto" },
            right: { xs: 8, sm: "auto" },
          }}
          onClick={handleAvatarClick}
        >
          <Avatar
            src={currentAvatarUrl}
            alt={user.displayName}
            sx={{
              height: "40px",
              width: "40px",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -4,
              right: -4,
              border: theme.palette.mode === "dark" ? "0px" : "1px solid white",
              backgroundColor:
                theme.palette.mode === "dark" ? "text.secondary" : "#f5f5f5",
              borderRadius: "50%",
              padding: "2px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 12,
                color:
                  theme.palette.mode === "dark"
                    ? "white"
                    : "rgba(0, 0, 0, 0.54)",
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            width: { xs: "100%", sm: 220, md: 280 },
            ml: { xs: 0, sm: 1, md: 1 },
            mt: { xs: 7, sm: 0 },
            order: { xs: 3, sm: 0 },
          }}
        >
          <Box sx={{ display: { xs: "none", sm: "block", md: "block" } }}>
            <TeacherSearch
              currentUserId={user.uid}
              styles={styles}
              theme={theme}
            />
          </Box>
        </Box>

        <Box
          sx={{
            ml: { xs: 0, sm: "auto", md: "auto" },
            alignSelf: { xs: "flex-start", sm: "center", md: "center" },
            mr: { xs: 0, sm: 2, md: 0 },
          }}
        >
          <Box sx={{ display: { xs: "none", sm: "block", md: "block" } }}>
            {user.school !== "roomStudent" && (
              <CustomInputSwitch
                optionIcons={[<PersonOutlineIcon />, <SchoolIcon />]}
                selectedIndex={
                  role === "student" || role === "undecided" ? 0 : 1
                }
                onIndexChange={(index) => {
                  const newRole = index === 0 ? "student" : "teacher";
                  setRole(newRole);
                  onUpdateUser({
                    accountType: newRole,
                  });
                  setResetCategory((prev) => !prev);
                }}
                options={["Elev", "Lärare"]}
                theme={theme}
                width="250px"
                mobileWidth="200px"
                tabletWidth="200px"
                height="38px"
                iconSpacing="10px"
                iconSize="1.2rem"
                activeColor={
                  theme.palette.mode === "dark" ? "#212c36" : "#909090"
                }
                nonActiveColor={
                  theme.palette.mode === "dark" ? "#3a454f" : "#ececec"
                }
                fontSize={
                  theme.palette.themeType === "allakando" ? "1rem" : "0.9rem"
                }
                fontWeight="500"
                borderRadius="50px"
              />
            )}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        <AccountTypeDialog
          open={showAccountTypeDialog}
          onClose={() => setShowAccountTypeDialog(false)}
          onSave={handleAccountSetup}
        />

        <Typography
          variant="h5"
          sx={{
            ...styles.header,
            fontSize: "1.3rem",
            fontWeight: "600",
            marginBottom: "8px",
            display: "none",
            color: theme.palette.mode === "dark" ? "#bbb" : "#797979",
          }}
        >
          {`Välkommen till ${theme.palette.themeType === "allakando" ? "Allakando AI" : "Studera.AI"}, ${user.displayName} `}{" "}
        </Typography>
        <Box sx={{ display: "none" }}>
          {rooms && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PeopleOutlineIcon sx={{ color: "text.secondary" }} />
              <Tooltip
                title={`Jag tillhör elevrum: ${rooms}`}
                placement="right"
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "500",
                    color: "text.secondary",
                    fontSize: "0.9rem",
                    display: "inline-block",
                  }}
                >
                  {rooms}
                </Typography>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/*
        <Box>
          <Button
            variant='contained'
            onClick={() => {
              const testData = {
                email: 'fredrikkk@example.com',
                name: 'Fredrik',
                partnerUserId: 'Fredrik-USER-12345678kk',
                school: 'Allakando',
                municipality: 'Allakando',
              };

              const encodedData = btoa(JSON.stringify(testData));
              window.location.href = `http://localhost:3000/app?userData=${encodedData}`;
            }}
            sx={{ mt: 2, mb: 2 }}
          >
            TESTA SSO LOGIN LOKALT
          </Button>

          <Typography sx={{ wordBreak: 'break-all' }}>
            <strong>Produktionslänk:</strong>
            <br />
            {`https://studera.ai/app/?userData=${btoa(
              JSON.stringify({
                email: 'linn.nerlund@allakando.se',
                name: 'Linn Nerlund',
                partnerUserId: 'Linn-USER-1234567891011',
                school: 'Allakando',
                municipality: 'Allakando',
              })
            )}`}
          </Typography>

          <Typography sx={{ wordBreak: 'break-all' }}>
            <strong>Produktionslänk:</strong>
            <br />
            {`https://studera.ai/app/?userData=${btoa(
              JSON.stringify({
                email: 'fredrik.fridlund@allakando.se',
                name: 'Fredrik Fridlund',
                partnerUserId: 'Fredrik-USER-1234567891011',
                school: 'Allakando',
                municipality: 'Allakando',
              })
            )}`}
          </Typography>
        </Box>
        */}

        <Box sx={{ mb: 5, display: "none" }}>
          <FeedbackPaper
            title="Ursäkta röran — vi bygger om"
            description="Tack vare er fantastiska feedback förbättrar vi just nu tjänsten! :)"
            onClick={() => setFeedbackDialogOpen(true)}
            theme={theme}
          />
        </Box>
        {/*
        <Box sx={{ display: "block" }}>
          <UsageBox userId={user.uid} styles={styles} />
        </Box>


*/}

        <Box sx={{ maxWidth: "1335px", margin: "0 auto" }}>
          <GridLayout user={user} role={role} />
        </Box>

        <Box
          sx={{
            ...styles.newBox,
            padding: "20px",
            width: { xs: "100%", md: "100%" },
            display:
              role === "teacher" || role === "substitute" ? "none" : "none",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", mb: 2, height: 40 }}
          >
            <Box sx={{ ...styles.iconBox }}>
              <MenuOpenIcon sx={{ ...styles.iconBoxColor }} />
            </Box>
            <Typography
              variant="h2"
              sx={{ ...styles.header, ...styles.dashboardHeader }}
            >
              Kurser
            </Typography>
          </Box>

          <CourseList
            compact={true}
            courseIds={[
              "d9RQjnU8wVGcaiDrO2dd",
              "hGrCbLXixh2kXy131Yf8",
              "DAmlDfx15ciBiOVAfXOj",
              "zRl9weyypH0guq6roPvt",
              "fgpOLak0BBqJP85zVj3z",
              "CdLx3L3F8LqrXI2vCX6G",
              "KYyUj9Q8Ye5nmpT9aUKF",
              "r7NnC08Grk6ylkKILsFa",
            ]}
          />
        </Box>
      </Box>
      {/*
      <NewFunctionDialog
        open={newFunctionDialogOpen}
        onClose={handleCloseNewFunctionDialog}
        title="NYHET: Öva på högskoleprovet med AI som stöd"
        videoUrl="https://sportkuriren.se/wp-content/uploads/hp.mp4"
        poster="https://sportkuriren.se/wp-content/uploads/hp.png"
        path="/app/hogskoleprov"
        description="Öva på högskoleprovet helt gratis med AI som stöd. Snart finns alla tidigare prov inlagda. Se hur det funkar på videon till höger."
        listItems={[
          {
            text: "Helt gratis!",
          },
          {
            text: "Praktisk vy över dina aktiva prov så du enkelt kan skifta",
          },
          {
            text: "Diskutera med vår AI när som helst för att öka din förståelse",
          },
          {
            text: "Använd tangentbordet för smidig navigering",
          },

          {
            text: "Smidig översikt över samtliga frågor så du enkelt kan hoppa fram och tillbaka",
          },

          {
            text: "Vi arbetar på att lägga in flera gamla högskoleprov! :)",
          },
        ]}
        useCases={[
          "Chatta med dokument",
          "Summera dokument",
          "Be om en förklaring",
        ]}
      />

*/}
      <FeedbackDialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        user={user}
      />
      <SettingsPopover
        user={user}
        isDark={theme.palette.mode === "dark"}
        openPopover={openPopover}
        anchorEl={anchorEl}
        handlePopoverClose={() => setAnchorEl(null)}
        styles={styles}
        theme={theme}
        setOverrideTheme={setOverrideTheme}
        avatarUrl={currentAvatarUrl}
        onAvatarUpdate={handleAvatarUpdate} // Add this line
      />
    </Box>
  );
};

export default Dashboard;
